import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Modal, TextareaAutosize, TextField } from "@material-ui/core"
import { Loading } from "../helpers/components"
import { Add, Delete, ArrowBackIos, ArrowForwardIos, AddPhotoAlternate, VolumeUp, VolumeOff } from "@material-ui/icons"
import { storageRef, fireStore } from "../firebase/firebase"
import { useAuth } from "../firebase/useAuth"
import ImageShowcaseModal from "./ImageShowCase"
import { SortableElement, SortableContainer } from "react-sortable-hoc"
import arrayMove from "array-move"
import noImg from "../assets/no-img.png"
import { useStore } from "../context/context"
import ReactPlayer from "react-player"
import moment from "moment"
import "moment-timezone"

export default function AddPost() {
  const [open, setOpen] = useState(false)

  const { dispatch } = useStore()
  const [currentUser] = useAuth()
  const userRef = fireStore.collection("users").doc(currentUser?.uid)
  
  const [images, setImages] = useState([])
	const [description, setDescription] = useState("")
  const [hashtags, setHashtags] = useState([])
  const [plannedFor, setPlannedFor] = useState(moment().tz("Europe/Paris").format("YYYY-MM-DDThh:mm"))
	const [hashtagInputValue, setHashtagInputValue] = useState("")
	const [imagesAreLoading, setImagesAreLoading] = useState(false)
	const [imageLoadingProgress, setImageLoadingProgress] = useState(0)
	const [imageSelected, setImageSelected] = useState(0)
  const [severalImages, setSeveralImages] = useState(images.length > 1)
  const [videoIsMuted, setVideoIsMuted] = useState(true)
  const [showImageShowcase, setShowImageShowcase] = useState(false)
  const [numberLimitOfPosts, setNumberLimitOfPosts] = useState(currentUser?.subscriptionType === "premium" ? 150 : 8)

  const VolumeButton = videoIsMuted ? VolumeOff : VolumeUp

  useEffect(() => images.length > 1 ? setSeveralImages(true) : setSeveralImages(false), [images])

  useEffect(() => { 
    setNumberLimitOfPosts(currentUser?.subscriptionType === "premium" ? 150 : 8) 
  }, [currentUser])

	const SortableImageItem = SortableElement(({ value, i }) => (
		<li>
      {value.type === "video" ? (
        <ReactPlayer 
          className="imagePreview"
          url={value.src || noImg}
          alt={value.src || noImg}
          playing={false}
          onClick={() => setImageSelected(i)}
        />
      ) : (
        <img
          className="imagePreview"
          src={value.src || noImg}
          alt={value.src || noImg}
          onClick={() => setImageSelected(i)}
        />
      )}
		</li>
	))
	const SortableImageList = SortableContainer(({ items, row }) => (
    <ul style={{
      width: "fit-content", 
      listStyle: "none", 
      display: "flex",
      flexFlow: "wrap", 
      flexDirection: row ? "row" : "column" 
    }}>
      {items.map((value, i) => (
        <SortableImageItem key={`item-${value?.src}`} index={i} i={i} value={value} />
      ))}
    </ul>
  ))
  const onSortEnd = ({ oldIndex, newIndex }) => setImages(arrayMove(images, oldIndex, newIndex))
  
	const uploadImage = async e => {
		setImagesAreLoading(true)
		const promises = []
    const files = e.target.files
		Array.from(files).forEach(file => {
      const fileType = file.type.split("/")[0]
			const imageRef = storageRef.child(
				`${currentUser?.displayName}/${file.name}`
			)
			const promise = imageRef.put(file)
      promises.push(promise)
      promise.on("state_changed", snap => {
        const progressPercent = parseInt(snap.bytesTransferred / snap.totalBytes * 100)
        setImageLoadingProgress(progressPercent)
      })
			promise.then(() => {
				imageRef.getDownloadURL().then(newImageUrl => {
					setImages(prevState => [...prevState, {
            src: newImageUrl,
            type: fileType
          }])
				})
      }).catch(err => console.log({errorInUpload: err}))
      Promise.all(promises).then(() => setImagesAreLoading(false))
    })
  }

  const handleDateChange = e => setPlannedFor(e.target.value)
  
  const handleDescriptionChange = e => setDescription(e.target.value)

	const handleHashtagsSubmit = e => {
		e.preventDefault()
		if (hashtagInputValue && !hashtags.some(ht => ht === hashtagInputValue)) {
      if(hashtagInputValue.includes(" ") && hashtagInputValue.includes("#")) {
        console.log("hey")
        setHashtags(ps => [...ps, ...hashtagInputValue.split(" ").filter(tag => tag.length > 0)])
      } else {
        setHashtags(ps => [...ps, `#${hashtagInputValue}`])
      }
			setHashtagInputValue("")
		}
	}

	const removeHashtag = (e, hashtag) => {
		setHashtags(ps => ps.filter(ht => ht !== hashtag))
	}

  const addPost = () => {
    if(currentUser?.posts?.length < numberLimitOfPosts) {
      if(images.length === 0 && !description) {
        return dispatch({ 
          type: "SET_NOTIFICATION",
          payload: { type: "warning", title: "Il faut au moins une image/vidéo ou une description pour ajouter un post" }
        })
      }
      userRef.update({ posts: [{
        images: images,
        description: description,
        done: false,
        hashtags: hashtags,
        plannedFor: plannedFor,
        post_id: `${Date.now()}${Math.random().toFixed(5).replace(".", "")}`
      }, ...currentUser?.posts] }).then(() => {
        dispatch({ type: "SET_NOTIFICATION", payload: "Post ajouté !" })
        setImages([])
        setDescription("")
        setHashtags([])
        onClose()
      })
    } else {
      dispatch({ type: "SET_NOTIFICATION", payload: {
        type: "warning",
        title: `${currentUser?.subscriptionType === "premium" ? "Vous avez atteint la limite maximal de posts" : "Pour pouvoir ajouter plus de posts, passez à la version premium (dans paramètres)"}`
      }})
    }
  }

	return (
		<div className="addPost">
			<button className="openAddPostModalBtn" onClick={() => setOpen(true)}>
				<Add className="addIcon" /> Plannifier un nouveau post
			</button>
			<Modal open={open} onClose={() => setOpen(false)} className="modalBox editPost">
				<motion.div
					className="modalContent"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
				>
					<div className="row">
						<div className="col imageSection">
              <ImageShowcaseModal
                open={showImageShowcase}
                onClose={() => setShowImageShowcase(false)}
                images={images}
                imageSelected={imageSelected}
                setImageSelected={setImageSelected}
                severalImages={severalImages}
              />
							<div className="imgContainer">
								{severalImages && (
									<ArrowBackIos
										className="imgArrow backward"
										onClick={() => setImageSelected(ps => ps === 0 ? images.length - 1 : ps - 1)}
									/>
								)}
								{images.length > 0 && (
									<button
										className="deleteImages imageBtn"
										onClick={() => setImages([])}
									>
										<Delete />
									</button>
								)}
								{images[imageSelected]?.type === "video" ? (
									<>
                    <ReactPlayer 
                      url={images[imageSelected]?.src}
                      className="mainImg"
                      playing loop
                      muted={videoIsMuted}
                      onClick={() => images.length > 0 && setShowImageShowcase(true)}
                    />
                    <button className="muteBtn imageBtn" onClick={() => setVideoIsMuted(ps => !ps)}>
                      <VolumeButton />
                    </button>
                  </>
								) : (
									<img
										src={images.length > 0 ? images[imageSelected]?.src : noImg}
										alt="main image"
                    className="mainImg"
                    onClick={() => images.length > 0 && setShowImageShowcase(true)}
									/>
								)}
								{imagesAreLoading && (
									<div className="imagesAreLoadingOverlay">
										<Loading />
										<span className="progressNumber">
											{imageLoadingProgress}%
										</span>
									</div>
								)}
								{severalImages && (
									<ArrowForwardIos
										className="imgArrow forward"
										onClick={() => setImageSelected(ps => ps === images.length - 1 ? 0 : ps + 1)}
									/>
								)}
							</div>
							<div className="imagesPreview">
								<SortableImageList
									items={images}
									onSortEnd={onSortEnd}
									axis="x"
									distance={3}
									row
								/>
							</div>
						</div>
						<div className="col editSection">
							<h2 className="editTitle">
								<span className="title">Ajouter un nouveau post</span>
							</h2>
							<div className="fileDateRow">
                <input
                  type="file"
                  id="fileInput"
                  className="fileUpload"
                  onChange={uploadImage}
                  multiple
                />
                <label htmlFor="fileInput" className="fileInputLabel">
                  <AddPhotoAlternate /> &nbsp; Choisir des fichiers
                </label>
                {/* <label htmlFor="dateInput" className="dateInputLabel">
                  {plannedFor}
                </label> */}
                <input
                  type="datetime-local"
                  className="plannedForBtn"
                  value={plannedFor}
                  onChange={handleDateChange}
                  id="dateInput"
                />
              </div>
							<div style={{ position: "relative" }}>
								<TextareaAutosize
									placeholder="Post description"
									rowsMin={5}
									className="instaDescriptionInput"
									value={description}
									onChange={handleDescriptionChange}
								/>
							</div>
							<form
								className="hashtagsInputContainer addPostHashtagInput"
								onSubmit={handleHashtagsSubmit}
							>
								<TextField
									placeholder="Hashtags (Appuyer sur Entrer pour ajouter)"
									className="hashtagsInput"
									value={hashtagInputValue}
									onChange={e => setHashtagInputValue(e.target.value)}
								/>
							</form>
							<div className="hashtags">
								{hashtags?.map((hashtag, i) => (
									<div
										className="hashtag"
										key={i}
										onClick={e => removeHashtag(e, hashtag)}
									>
										{hashtag}
									</div>
								))}
							</div>
						</div>
          </div>
          <button className="saveBtn btn" onClick={addPost}>
            Ajouter le post
          </button>
				</motion.div>
			</Modal>
		</div>
	)
}
